import React from 'react';

// Renders the server-built referral `journey` (ca-backend/utils/referralService.js).
// Used by staff (Referral Partners → Leads) and partners (client portal), so both
// always see the same stages, exceptions and notes. Never derive stages here.

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const LIGHT = {
  bg: '#f8faff', card: '#fff', border: '#e8edf5', text: '#0f1f45', text2: '#4a5568', text3: '#94a3b8',
  done: '#16a34a', current: '#1a3a6b',
};

const EXCEPTION_TONE = {
  lost: { color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
  cancelled: { color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
  verification: { color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
};

const INVOICE_TONE = {
  paid: '#16a34a', partially_paid: '#d97706', cancelled: '#dc2626', draft: '#64748b', sent: '#2563eb', overdue: '#dc2626',
};

const COMMISSION_TONE = {
  Credited: '#16a34a', 'Paid out': '#16a34a', 'Partly paid out': '#16a34a',
  'Awaiting approval': '#2563eb', 'Awaiting payment': '#d97706',
  'Invoice cancelled': '#dc2626', Rejected: '#dc2626',
};

// showConflicts: staff-only notes (e.g. "invoice cancelled after ₹x was paid out").
export default function ReferralJourney({ journey, T: theme, compact = false, showConflicts = false }) {
  const T = { ...LIGHT, ...(theme || {}) };
  if (!journey) return null;
  const { steps = [], exception, invoices = [], commissions = [] } = journey;
  const ex = exception && (EXCEPTION_TONE[exception.type] || EXCEPTION_TONE.lost);

  return (
    <div>
      {exception && (
        <div style={{ background: ex.bg, border: `1px solid ${ex.border}`, color: ex.color, borderRadius: 10, padding: '8px 12px', fontSize: 12, marginBottom: steps.length ? 12 : 0 }}>
          <strong>{exception.label}</strong>{exception.note ? ` — ${exception.note}` : ''}
        </div>
      )}

      {steps.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          {steps.map((s, i) => {
            const done = s.state === 'done';
            const cur = s.state === 'current';
            const color = done ? T.done : cur ? T.current : T.border;
            return (
              <React.Fragment key={s.key}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0, flex: '0 0 auto', width: compact ? 44 : 64 }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: done ? T.done : cur ? T.current : T.card, border: `2px solid ${color}`,
                  }}>
                    {done
                      ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                      : <div style={{ width: 7, height: 7, borderRadius: '50%', background: cur ? '#fff' : T.text3 }} />}
                  </div>
                  <span style={{ fontSize: 10, marginTop: 4, color: done || cur ? T.text : T.text3, fontWeight: cur ? 700 : 500, whiteSpace: 'nowrap' }}>{s.label}</span>
                  {!compact && s.note && (
                    <span title={s.note} style={{ fontSize: 9.5, color: T.text3, textAlign: 'center', lineHeight: 1.3, marginTop: 2, maxWidth: 80, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {s.note}
                    </span>
                  )}
                </div>
                {i < steps.length - 1 && (
                  <div style={{ flex: 1, height: 2, marginTop: 11, background: steps[i + 1].state !== 'todo' ? T.done : T.border, minWidth: 8 }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {(invoices.length > 0 || commissions.length > 0) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
          {invoices.map((inv) => (
            <span key={`i${inv.id}`} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 20, border: `1px solid ${T.border}`, color: T.text2, background: T.card,
              textDecoration: inv.status === 'cancelled' ? 'line-through' : 'none' }}>
              {inv.number} · {fmt(inv.total)}
              <span style={{ color: INVOICE_TONE[inv.status] || T.text3, fontWeight: 700, marginLeft: 5, textDecoration: 'none', display: 'inline-block' }}>
                {String(inv.status || '').replace(/_/g, ' ')}
              </span>
            </span>
          ))}
          {commissions.map((c) => (
            <span key={`c${c.id}`} title={c.conflict || undefined} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 20, background: T.bg, color: T.text2 }}>
              Commission {fmt(c.status === 'approved' ? c.net : c.gross)}
              {c.tds > 0 && c.status === 'approved' ? ` (TDS ${fmt(c.tds)})` : ''}
              <span style={{ color: COMMISSION_TONE[c.display] || T.text3, fontWeight: 700, marginLeft: 5 }}>{c.display}</span>
              {c.paid_out > 0 && c.display === 'Partly paid out' ? ` · ${fmt(c.paid_out)} paid` : ''}
            </span>
          ))}
          {showConflicts && commissions.filter((c) => c.conflict).map((c) => (
            <div key={`x${c.id}`} style={{ flexBasis: '100%', fontSize: 11, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '6px 10px' }}>
              ⚠ {c.conflict}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
